import { Fragment, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Modal, useApiQuery, type Column } from 'grabbitbot-legacy-ui'
import { Table } from '../../components/DataTable'
import { plans as plansApi, telegram } from '../../api/fee'
import type { Plan, RequiredPlan, TelegramChatMember, TelegramChatUserManager } from '../../api/types'
import { Notice, PageTitle, QueryState } from '../../components/common'
import { useProject } from '../../layout/projectContext'
import { formatDateTime } from '../../lib/format'
import { useMoneyAction } from '../../lib/useMoneyAction'

const GRACE_OPTIONS: [number, string][] = [
  [0, 'No grace period'],
  [86400, '1 day'],
  [172800, '2 day'],
  [259200, '3 day'],
  [604800, '7 day'],
]
const DEFAULT_GRACE = 604800

function planName(plans: Plan[], id: string): string {
  const p = plans.find((x) => x.id === id)
  return p ? `${p.name} (${id})` : `Unknown plan (${id})`
}

/** "Subscriptions needed" — the legacy nested and/or text. */
function RequiredPlansText({ required, plans, projectId }: { required: RequiredPlan[]; plans: Plan[]; projectId: string }) {
  if (!required.length) return <span className="portal-text-muted">No subscriptions needed</span>
  const link = (id: string) => (
    <Link to={`/projects/${encodeURIComponent(projectId)}/subscriptions/${encodeURIComponent(id)}`}>{planName(plans, id)}</Link>
  )
  return (
    <span>
      {required.map((r, i) => (
        <Fragment key={i}>
          {i > 0 && ' and '}
          {r.type === 'group' ? (
            <span>
              (
              {(r.planSettings ?? []).map((s, j) => (
                <Fragment key={j}>
                  {j > 0 && ` ${r.logic ?? 'or'} `}
                  {link(s.planId)}
                </Fragment>
              ))}
              )
            </span>
          ) : (
            link(r.planId)
          )}
        </Fragment>
      ))}
    </span>
  )
}

/**
 * Port of `TelegramChatUserManagerListComponent`
 * (`projects/:projectId/telegram-chat-user-manager[/:managerId]`, admin):
 * Telegram-group gating by paid subscription (B7). List, add (name + chat
 * id), edit incl. the required-plans editor (plans and OR groups, grace
 * period), and the view modal (`:managerId` deep link) with chat info,
 * member list, per-member detail and "sync to Telegram". A chat owned by
 * another manager or project is refused by the backend with HTTP 200
 * `{"error": …}` — shown as an error, never as success.
 */
export function TelegramChatUserManagerPage() {
  const { projectId, isAdmin } = useProject()
  const { managerId } = useParams<{ managerId?: string }>()
  const navigate = useNavigate()
  const list = useApiQuery(() => telegram.managers(projectId), [projectId])
  const planList = useApiQuery(() => plansApi.search(projectId), [projectId])
  const [editing, setEditing] = useState<TelegramChatUserManager | 'new' | null>(null)
  const base = `/projects/${encodeURIComponent(projectId)}/telegram-chat-user-manager`
  const plans = planList.data ?? []

  const columns: Column<TelegramChatUserManager>[] = [
    { key: 'name', label: 'User Manager name', format: (_v, m) => <Link to={`${base}/${encodeURIComponent(m.id)}`}>{m.name}</Link> },
    { key: 'chatId', label: 'Chat ID', align: 'right' },
    {
      key: 'requiredPlans',
      label: 'Subscriptions needed',
      sortable: false,
      format: (_v, m) => <RequiredPlansText required={m.requiredPlans ?? []} plans={plans} projectId={projectId} />,
    },
    {
      key: 'actions',
      label: '',
      sortable: false,
      format: (_v, m) =>
        isAdmin ? (
          <button type="button" onClick={() => setEditing(m)}>
            Edit
          </button>
        ) : null,
    },
  ]
  const selected = managerId ? (list.data?.find((m) => m.id === managerId) ?? null) : null

  return (
    <div className="portal-card">
      <PageTitle title="Telegram Chat User Manager">
        {isAdmin && (
          <button type="button" onClick={() => setEditing('new')}>
            Add User Manager
          </button>
        )}
      </PageTitle>
      <p className="portal-notice portal-notice--info">
        Invite <strong>FeeSubscriptionChatManagerBot</strong> as an administrator to all Telegram Chats you want to manage.
        Invite it BEFORE you start to invite members, so it can see every member.
      </p>
      <QueryState loading={list.loading} error={list.error}>
        <div className="portal-table-scroll">
          <Table columns={columns} rows={list.data ?? []} rowKey={(m) => m.id} />
        </div>
        {list.data?.length === 0 && <p className="portal-text-muted">No user managers yet.</p>}
      </QueryState>
      {selected && (
        <Modal open title={selected.name} onClose={() => navigate(base)} className="portal-modal-wide">
          <ManagerView projectId={projectId} manager={selected} plans={plans} />
        </Modal>
      )}
      {editing && (
        <ManagerEditor
          projectId={projectId}
          manager={editing === 'new' ? null : editing}
          plans={plans}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            list.refetch()
          }}
        />
      )}
    </div>
  )
}

function GraceSelect({ value, onChange, label }: { value: number; onChange: (v: number) => void; label: string }) {
  const known = GRACE_OPTIONS.some(([v]) => v === value)
  return (
    <select aria-label={label} value={value} onChange={(e) => onChange(Number(e.target.value))}>
      {!known && <option value={value}>{value} s</option>}
      {GRACE_OPTIONS.map(([v, l]) => (
        <option key={v} value={v}>
          {l}
        </option>
      ))}
    </select>
  )
}

function PlanSelect({ plans, value, onChange, label }: { plans: Plan[]; value: string; onChange: (v: string) => void; label: string }) {
  return (
    <select aria-label={label} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">— plan —</option>
      {!plans.some((p) => p.id === value) && value && <option value={value}>Unknown plan ({value})</option>}
      {plans.map((p) => (
        <option key={p.id} value={p.id}>
          {p.name}
        </option>
      ))}
    </select>
  )
}

function ManagerEditor({
  projectId,
  manager,
  plans,
  onClose,
  onSaved,
}: {
  projectId: string
  manager: TelegramChatUserManager | null
  plans: Plan[]
  onClose: () => void
  onSaved: () => void
}) {
  const isNew = !manager
  const [name, setName] = useState(manager?.name ?? '')
  const [chatId, setChatId] = useState(manager?.chatId ?? '')
  const [required, setRequired] = useState<RequiredPlan[]>(() => structuredClone(manager?.requiredPlans ?? []))
  const actions = useMoneyAction()

  const update = (i: number, next: RequiredPlan) => setRequired(required.map((r, j) => (j === i ? next : r)))

  const save = async () => {
    const body: Partial<TelegramChatUserManager> = { name: name.trim(), chatId: chatId.trim() }
    if (!isNew) body.requiredPlans = required
    const r = await actions.run('save', JSON.stringify(body), () =>
      isNew ? telegram.addManager(projectId, body) : telegram.modifyManager(projectId, manager!.id, body),
    )
    if (r) onSaved()
  }

  const title = isNew ? 'Add User Manager' : 'Edit User Manager'
  return (
    <Modal open title={title} onClose={onClose} className="portal-modal-wide">
      <form
        className="portal-form fs-form-wide"
        aria-label={title}
        onSubmit={(e) => {
          e.preventDefault()
          void save()
        }}
      >
        <label>
          Name
          <input value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label>
          Chat ID
          <input value={chatId} onChange={(e) => setChatId(e.target.value)} />
        </label>
        {!isNew && (
          <fieldset className="fs-fieldset">
            <legend>Subscriptions needed</legend>
            {required.map((r, i) =>
              r.type === 'group' ? (
                <div key={i} className="fs-panel">
                  <strong>Group ({r.logic ?? 'or'})</strong>
                  {(r.planSettings ?? []).map((s, j) => (
                    <div key={j} className="portal-input-group">
                      {j > 0 && <span className="portal-input-unit">{r.logic ?? 'or'}</span>}
                      <PlanSelect
                        plans={plans}
                        label={`Group ${i + 1} plan ${j + 1}`}
                        value={s.planId}
                        onChange={(v) => update(i, { ...r, planSettings: (r.planSettings ?? []).map((x, k) => (k === j ? { ...x, planId: v } : x)) })}
                      />
                      <GraceSelect
                        label={`Group ${i + 1} plan ${j + 1} grace period`}
                        value={Number(s.gracePeriod)}
                        onChange={(v) =>
                          update(i, { ...r, planSettings: (r.planSettings ?? []).map((x, k) => (k === j ? { ...x, gracePeriod: v } : x)) })
                        }
                      />
                      <button
                        type="button"
                        aria-label={`Remove plan ${j + 1} from group ${i + 1}`}
                        onClick={() => update(i, { ...r, planSettings: (r.planSettings ?? []).filter((_, k) => k !== j) })}
                      >
                        −
                      </button>
                    </div>
                  ))}
                  <div className="portal-inline-actions">
                    <button
                      type="button"
                      onClick={() =>
                        update(i, { ...r, planSettings: [...(r.planSettings ?? []), { planId: '', gracePeriod: DEFAULT_GRACE, type: 'plan' }] })
                      }
                    >
                      Add plan to group
                    </button>
                    <button type="button" onClick={() => setRequired(required.filter((_, j) => j !== i))}>
                      Remove group
                    </button>
                  </div>
                </div>
              ) : (
                <div key={i} className="portal-input-group">
                  <PlanSelect plans={plans} label={`Required plan ${i + 1}`} value={r.planId} onChange={(v) => update(i, { ...r, planId: v })} />
                  <GraceSelect
                    label={`Required plan ${i + 1} grace period`}
                    value={Number(r.gracePeriod)}
                    onChange={(v) => update(i, { ...r, gracePeriod: v })}
                  />
                  <button type="button" aria-label={`Remove required plan ${i + 1}`} onClick={() => setRequired(required.filter((_, j) => j !== i))}>
                    −
                  </button>
                </div>
              ),
            )}
            <div className="portal-inline-actions">
              <button type="button" onClick={() => setRequired([...required, { planId: '', gracePeriod: DEFAULT_GRACE, type: 'plan' }])}>
                Add plan
              </button>
              <button
                type="button"
                onClick={() =>
                  setRequired([
                    ...required,
                    { planId: '', gracePeriod: 0, type: 'group', logic: 'or', planSettings: [{ planId: '', gracePeriod: DEFAULT_GRACE, type: 'plan' }] },
                  ])
                }
              >
                Add OR group
              </button>
            </div>
          </fieldset>
        )}
        <div className="portal-inline-actions">
          <button type="submit" disabled={actions.isBusy('save')}>
            {actions.isBusy('save') ? 'Saving…' : 'Save'}
          </button>
          <button type="button" onClick={onClose}>
            Cancel
          </button>
        </div>
        <Notice feedback={actions.feedbackFor('save')} />
      </form>
    </Modal>
  )
}

function yesNo(v: unknown): string {
  return v ? 'Yes' : 'No'
}

function MemberDetail({ projectId, managerId, userId }: { projectId: string; managerId: string; userId: string }) {
  const q = useApiQuery(() => telegram.member(projectId, managerId, userId), [projectId, managerId, userId])
  const actions = useMoneyAction()
  const v = q.data
  return (
    <QueryState loading={q.loading} error={q.error}>
      {v && (
        <div className={`fs-panel ${Number(v.status) === 1 ? 'fs-panel--success' : 'fs-panel--danger'}`}>
          <p>
            <strong>{Number(v.status) === 1 ? 'User can use this Telegram chat' : "User can't use this Telegram chat"}</strong>{' '}
            <button
              type="button"
              disabled={actions.isBusy('sync')}
              onClick={() =>
                void actions.run('sync', userId, () => telegram.syncMember(projectId, managerId, userId), () =>
                  actions.setFeedback('sync', { tone: 'success', text: 'Synced to Telegram.' }),
                )
              }
            >
              Sync now to Telegram
            </button>
          </p>
          <dl className="portal-kv">
            <dt>Last command</dt>
            <dd>
              {v.member.lastCommand ?? '-'} {formatDateTime(v.member.lastCommandAt)}
            </dd>
            <dt>Is admin?</dt>
            <dd>{yesNo(v.isAdmin)}</dd>
            <dt>Is Bot?</dt>
            <dd>{yesNo(v.isBot)}</dd>
            <dt>Has Telegram Connection?</dt>
            <dd>{yesNo(v.hasTelegramConnection)}</dd>
            <dt>Telegram Connection shared with this project?</dt>
            <dd>{yesNo(v.telegramConnectionSharedWithProject)}</dd>
            <dt>Email</dt>
            <dd>{v.email ?? (v.hasTelegramConnection ? 'Not shared with this project' : 'Not known')}</dd>
          </dl>
          {v.requiredPlans === null && !v.isAdmin && !v.isBot && <p className="portal-notice portal-notice--error">Required plans can&apos;t be checked!</p>}
          <Notice feedback={actions.feedbackFor('sync')} />
        </div>
      )}
    </QueryState>
  )
}

function ManagerView({ projectId, manager, plans }: { projectId: string; manager: TelegramChatUserManager; plans: Plan[] }) {
  const info = useApiQuery(() => telegram.chatInfo(projectId, manager.id), [projectId, manager.id])
  const members = useApiQuery(() => telegram.members(projectId, manager.id), [projectId, manager.id])
  const [open, setOpen] = useState<string | null>(null)
  const actions = useMoneyAction()

  const columns: Column<TelegramChatMember & { idx: number }>[] = [
    { key: 'idx', label: '#', align: 'right', numeric: true },
    { key: 'userId', label: 'ID', align: 'right', numeric: true },
    { key: 'name', label: 'Name', sortValue: (m) => `${m.lastName ?? ''} ${m.firstName ?? ''}`, format: (_v, m) => [m.lastName, m.firstName].filter(Boolean).join(' ') },
    { key: 'isBot', label: 'Is bot?', format: (v) => yesNo(v) },
    { key: 'status', label: 'Status' },
    {
      key: 'lastCommand',
      label: 'Last command',
      format: (_v, m) => (m.lastCommand ? `${m.lastCommand} ${formatDateTime(m.lastCommandAt)}` : '-'),
    },
    {
      key: 'details',
      label: '',
      sortable: false,
      format: (_v, m) => (
        <button type="button" className="fs-link-button" onClick={() => setOpen(open === String(m.userId) ? null : String(m.userId))}>
          {open === String(m.userId) ? 'Hide details' : 'Show details'}
        </button>
      ),
    },
  ]

  return (
    <div className="fs-modal-body">
      <dl className="portal-kv">
        <dt>ID</dt>
        <dd>{manager.id}</dd>
        <dt>Name</dt>
        <dd>{manager.name}</dd>
        <dt>Chat ID</dt>
        <dd>{manager.chatId}</dd>
        <dt>Chat</dt>
        <dd>{info.loading ? 'loading …' : info.data ? `${info.data.title ?? '-'} (${info.data.type ?? '-'})` : '-'}</dd>
        <dt>Subscriptions needed</dt>
        <dd>
          <RequiredPlansText required={manager.requiredPlans ?? []} plans={plans} projectId={projectId} />
        </dd>
      </dl>
      <div className="portal-inline-actions">
        <button
          type="button"
          disabled={actions.isBusy('info')}
          onClick={() => void actions.run('info', manager.id, () => telegram.syncChatInfo(projectId, manager.id), () => info.refetch())}
        >
          Sync chat info from Telegram
        </button>
        <button
          type="button"
          disabled={actions.isBusy('all')}
          onClick={() =>
            void actions.run('all', manager.id, () => telegram.syncAllMembers(projectId, manager.id), () =>
              actions.setFeedback('all', { tone: 'success', text: 'Sync started, it may take a few minutes!' }),
            )
          }
        >
          Sync all members to Telegram
        </button>
      </div>
      <Notice feedback={actions.feedbackFor('info') ?? actions.feedbackFor('all')} />
      <QueryState loading={members.loading} error={members.error}>
        <div className="portal-table-scroll">
          <Table
            columns={columns}
            rows={(members.data ?? []).map((m, i) => ({ ...m, idx: i + 1 }))}
            rowKey={(m) => String(m.userId ?? m.idx)}
            rowClassName={(m) => (Number(m.chatStatus) === 0 ? 'fs-row--muted' : Number(m.chatStatus) === -1 ? 'fs-row--danger-text' : undefined)}
            renderExpanded={(m) =>
              open === String(m.userId) ? (
                <tr className="portal-detail-row">
                  <td colSpan={columns.length}>
                    <MemberDetail projectId={projectId} managerId={manager.id} userId={String(m.userId)} />
                  </td>
                </tr>
              ) : null
            }
          />
        </div>
      </QueryState>
    </div>
  )
}
